import { create } from "zustand";

/** User shape returned by the auth API and stored in next-auth session */
export interface SessionUser {
  id?: string | number | null;
  username?: string | null;
  email?: string | null;
  role_name?: string | null;
  [key: string]: unknown;
}

interface UserState {
  id: string | null;
  username: string | null;
  email: string | null;
  role_name: string | null;
  isLoggedIn: boolean;
  _hasHydrated: boolean;
  /** Sync store from next-auth session user (call when session is available). */
  setFromSession: (user: SessionUser | null) => void;
  /** Clear user state (e.g. on signOut). */
  logout: () => void;
  setHasHydrated: (state: boolean) => void;
}

function userFromSession(user: SessionUser | null): Pick<UserState, "id" | "username" | "email" | "role_name" | "isLoggedIn"> {
  if (!user) {
    return {
      id: null,
      username: null,
      email: null,
      role_name: null,
      isLoggedIn: false,
    };
  }
  const id = user.id != null ? String(user.id) : null;
  return {
    id,
    username: user.username ?? null,
    email: user.email ?? null,
    role_name: user.role_name ?? null,
    isLoggedIn: true,
  };
}

export const useUserStore = create<UserState>((set) => ({
  id: null,
  username: null,
  email: null,
  role_name: null,
  isLoggedIn: false,
  _hasHydrated: false,
  setFromSession: (user) => set((state) => ({ ...state, ...userFromSession(user) })),
  logout: () =>
    set({
      id: null,
      username: null,
      email: null,
      role_name: null,
      isLoggedIn: false,
    }),
  setHasHydrated: (state) => set({ _hasHydrated: state }),
}));

export const useHasHydrated = () => useUserStore((s) => s._hasHydrated);
